import React, { useState, useEffect } from "react";
import { partService, Part } from "../../Services/PartService";
import api from "../../Services/api";
import {
    Vendor,
    Consignee,
    Purchaser,
    ManualPart,
    CCIFormData,
    DropdownOption,
    DRAFT_FORM_KEY,
    DRAFT_PARTS_KEY
} from "./types";
import { DeviceCompatibilityNotice } from '../DeviceCompatibilityNotice';
import { EntitySection } from "./EntitySection";
import { ShipmentDetailsSection } from "./ShipmentDetailsSection";
import { LogoSection } from "./LogoSection";
import { DraftNotification } from "./DraftNotification";
import { ErrorNotification } from "./ErrorNotification";
import { FormActions } from "./FormActions";
import { InvoiceInfoSection } from "./InvoiceInfoSection";
import { InvoicePartsSection } from "./InvoicePartsSection";
import { FormExtras } from '../FormExtras';

interface SubscriptionData {
    tier?: string;
    status?: string;
    activeSubscription?: boolean;
}

export const CCICreation: React.FC = () => {
    const [parts, setParts] = useState<Part[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [consignees, setConsignees] = useState<Consignee[]>([]);
    const [purchasers, setPurchasers] = useState<Purchaser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [manualParts, setManualParts] = useState<ManualPart[]>([]);
    const [isViewportTooSmall, setIsViewportTooSmall] = useState(false);
    const MINIMUM_VIEWPORT_WIDTH = 1280;
    const [useLogo, setUseLogo] = useState(false);
    const [hasLogo, setHasLogo] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [formData, setFormData] = useState<CCIFormData>({
        vendorId: "",
        consigneeId: "",
        purchaserId: "",
        manualVendor: {
            name: "",
            address: "",
            city: "",
            stateProvince: "",
            postalCode: "",
            country: ""
        },
        manualConsignee: {
            name: "",
            address: "",
            city: "",
            stateProvince: "",
            postalCode: "",
            country: ""
        },
        manualPurchaser: {
            name: "",
            address: "",
            city: "",
            stateProvince: "",
            postalCode: "",
            country: ""
        },
        useManualVendor: false,
        useManualConsignee: false,
        useManualPurchaser: false,
        dateOfDirectShipment: new Date().toISOString().split('T')[0],
        currency: "USD",
        shipmentDetails: {
            transportation: "",
            otherReferences: "",
            countryOfTransshipment: "",
            conditionsOfSale: "",
            netWeight: undefined,
            grossWeight: undefined,
            weightUnit: "kg" // Default to kg
        },
        deductions: {
            transportationToCanada: "",
            constructionCosts: "",
            exportPackaging: ""
        },
        additions: {
            transportationToDirectJourney: "",
            commissions: "",
            exportPacking: ""
        },
        other: {
            royaltyPayments: false,
            suppliedGoods: false
        },
        usmcaSignaturePermission: false
    });

    const [isGenerating, setIsGenerating] = useState(false);
    const [hasDraft, setHasDraft] = useState(false);

    useEffect(() => {
        // Check viewport width on initial render
        checkViewportWidth();

        // Add event listener for window resize
        window.addEventListener('resize', checkViewportWidth);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('resize', checkViewportWidth);
        };
    }, []);

    const checkViewportWidth = () => {
        setIsViewportTooSmall(window.innerWidth < MINIMUM_VIEWPORT_WIDTH);
    };

    useEffect(() => {
        fetchData();
        fetchSubscriptionData();
    }, []);

    // Load draft from localStorage on component mount
    useEffect(() => {
        loadDraft();
    }, []);

    // Save form data to localStorage whenever it changes
    useEffect(() => {
        saveDraft();
    }, [formData, manualParts, useLogo]);

    const fetchSubscriptionData = async () => {
        setSubscriptionLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                setSubscriptionData(null);
                return;
            }

            const response = await api.get("/subscription/status", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setSubscriptionData(response.data);
        } catch (error) {
            console.error("Error fetching subscription data:", error);
            setSubscriptionData(null);
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const hasPremiumAccess = () => {
        if (!subscriptionData) return false;
        return subscriptionData.tier?.toLowerCase() === 'premium' &&
            subscriptionData.activeSubscription;
    };

    const hasProfessionalOrPremiumAccess = () => {
        if (!subscriptionData) return false;
        const tier = subscriptionData.tier?.toLowerCase();
        console.log("professional tier:", tier === 'professional');
        console.log("subscription active:", subscriptionData.activeSubscription);
        return (tier === 'professional' || tier === 'premium') &&
            subscriptionData.activeSubscription;
    };

    const handleLogoChange = (hasLogo: boolean) => {
        setHasLogo(hasLogo);
        // If user has a logo, automatically set useLogo to true
        if (hasLogo) {
            setUseLogo(true);
        } else {
            // If user doesn't have a logo, make sure useLogo is false
            setUseLogo(false);
        }
    };


    const handleOtherChange = (field: string, value: boolean) => {
        setFormData(prev => ({
            ...prev,
            other: {
                ...prev.other,
                [field]: value
            }
        }));
    };


    const vendorOptions: DropdownOption[] = vendors.map(vendor => ({
        id: vendor._id,
        primaryText: vendor.name,
        secondaryText: `${vendor.address}, ${vendor.city}, ${vendor.state} ${vendor.zipCode}`
    }));

    const consigneeOptions: DropdownOption[] = consignees.map(consignee => ({
        id: consignee._id,
        primaryText: consignee.name,
        secondaryText: `${consignee.address}, ${consignee.city}, ${consignee.state} ${consignee.zipCode}`
    }));

    const purchaserOptions: DropdownOption[] = purchasers.map(purchaser => ({
        id: purchaser._id,
        primaryText: purchaser.name,
        secondaryText: `${purchaser.address}, ${purchaser.city}, ${purchaser.state} ${purchaser.zipCode}`
    }));

    const addMultipleParts = (newParts: ManualPart[]) => {
        setManualParts(prevParts => [...prevParts, ...newParts]);
    };

    const saveDraft = () => {
        try {
            if (formData.vendorId === "" && formData.consigneeId === "" &&
                formData.purchaserId === "" && manualParts.length === 0) {
                return;
            }

            localStorage.setItem(DRAFT_FORM_KEY, JSON.stringify({
                ...formData,
                useLogo
            }));
            localStorage.setItem(DRAFT_PARTS_KEY, JSON.stringify(manualParts));
            setHasDraft(true);
        } catch (error) {
            console.error("Error saving draft:", error);
        }
    };

    const loadDraft = () => {
        try {
            const savedForm = localStorage.getItem(DRAFT_FORM_KEY);
            const savedParts = localStorage.getItem(DRAFT_PARTS_KEY);

            if (savedForm) {
                const parsedForm = JSON.parse(savedForm);

                // Ensure all required properties exist
                const updatedForm = {
                    ...formData, // Start with default values
                    ...parsedForm, // Override with saved values
                    // Ensure manual entities exist
                    manualVendor: parsedForm.manualVendor || {
                        name: "", address: "", city: "", stateProvince: "", postalCode: "", country: ""
                    },
                    manualConsignee: parsedForm.manualConsignee || {
                        name: "", address: "", city: "", stateProvince: "", postalCode: "", country: ""
                    },
                    manualPurchaser: parsedForm.manualPurchaser || {
                        name: "", address: "", city: "", stateProvince: "", postalCode: "", country: ""
                    },
                    useManualVendor: parsedForm.useManualVendor || false,
                    useManualConsignee: parsedForm.useManualConsignee || false,
                    useManualPurchaser: parsedForm.useManualPurchaser || false,
                    other: parsedForm.other || {
                        royaltyPayments: false,
                        suppliedGoods: false
                    },
                    usmcaSignaturePermission: parsedForm.usmcaSignaturePermission || false
                };

                setFormData(updatedForm);

                // Set logo usage if it was saved
                if (parsedForm.useLogo !== undefined) {
                    setUseLogo(parsedForm.useLogo);
                }

                setHasDraft(true);
            }

            if (savedParts) {
                setManualParts(JSON.parse(savedParts));
                setHasDraft(true);
            }
        } catch (error) {
            console.error("Error loading draft:", error);
        }
    };

    // Clear the saved draft
    const clearDraft = () => {
        localStorage.removeItem(DRAFT_FORM_KEY);
        localStorage.removeItem(DRAFT_PARTS_KEY);
        setHasDraft(false);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch all required data in parallel
            const [partsData, vendorsData, consigneesData, purchasersData] = await Promise.all([
                partService.getAllParts(),
                api.get("/vendors").then(res => res.data),
                api.get("/consignees").then(res => res.data),
                api.get("/purchasers").then(res => res.data)
            ]);

            setParts(partsData);
            setVendors(vendorsData);
            setConsignees(consigneesData);
            setPurchasers(purchasersData);
            setError(null);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent as keyof CCIFormData] as any,
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDropdownChange = (name: string, value: string) => {
        handleInputChange({
            target: { name, value }
        } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleDeductionsChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            deductions: {
                ...prev.deductions,
                [field]: value
            }
        }));
    };

    const handleAdditionsChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            additions: {
                ...prev.additions,
                [field]: value
            }
        }));
    };

    // Handler for toggle switches
    const handleToggleManualEntry = (field: 'vendor' | 'consignee' | 'purchaser') => {
        const toggleField = `useManual${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof CCIFormData;
        setFormData(prev => ({
            ...prev,
            [toggleField]: !prev[toggleField]
        }));
    };

    // Handler for manual entity fields
    const handleManualEntityChange = (
        entity: 'vendor' | 'consignee' | 'purchaser',
        field: 'name' | 'address' | 'city' | 'stateProvince' | 'postalCode' | 'country',
        value: string
    ) => {
        const entityField = `manual${entity.charAt(0).toUpperCase() + entity.slice(1)}` as keyof CCIFormData;
        setFormData(prev => ({
            ...prev,
            [entityField]: {
                ...(prev[entityField] as any),
                [field]: value
            }
        }));
    };

    const handleCurrencyChange = (currency: string) => {
        setFormData(prev => ({
            ...prev,
            currency
        }));
    };

    const addNewPart = () => {
        const newPart: ManualPart = {
            id: Date.now().toString(),
            partNumber: "",
            description: "",
            hsCode: "",
            countryOfOrigin: "",
            quantity: 1,
            unitPrice: 0,
            usmca: "No",
            usmcaOriginCriteria: "",
            usmcaQualificationMethod: "",
        };
        setManualParts([...manualParts, newPart]);
    };

    const removePart = (id: string) => {
        setManualParts(manualParts.filter(part => part.id !== id));
    };

    const updatePart = (id: string, field: keyof ManualPart, value: any) => {
        setManualParts(prevParts =>
            prevParts.map(part =>
                part.id === id ? { ...part, [field]: value } : part
            )
        );
    };

    const updatePartFull = (id: string, updatedPart: Partial<ManualPart>) => {
        setManualParts(prevParts =>
            prevParts.map(part =>
                part.id === id ? { ...part, ...updatedPart } : part
            )
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (manualParts.length === 0) {
            setError("Please add at least one part to the invoice.");
            return;
        }

        // Validate required fields
        if (!formData.useManualVendor && !formData.vendorId) {
            setError("Please select a vendor or enter vendor information manually.");
            return;
        } else if (formData.useManualVendor) {
            const { name, address, city, stateProvince, postalCode, country } = formData.manualVendor
            if (!name || !address || !city || !stateProvince || !postalCode || !country) {
                setError("Please enter complete vendor information.");
                return;
            }
        }

        if (!formData.useManualConsignee && !formData.consigneeId) {
            setError("Please select a consignee or enter consignee information manually.");
            return;
        } else if (formData.useManualConsignee) {
            const { name, address, city, stateProvince, postalCode, country } = formData.manualConsignee;
            if (!name || !address || !city || !stateProvince || !postalCode || !country) {
                setError("Please enter complete consignee information.");
                return;
            }
        }

        if (!formData.useManualPurchaser && !formData.purchaserId) {
            setError("Please select a purchaser or enter purchaser information manually.");
            return;
        } else if (formData.useManualPurchaser) {
            const { name, address, city, stateProvince, postalCode, country } = formData.manualPurchaser;
            if (!name || !address || !city || !stateProvince || !postalCode || !country) {
                setError("Please enter complete purchaser information.");
                return;
            }
        }

        // Check if any part numbers are invalid
        const invalidParts = manualParts.filter(part => !part.partNumber.trim());
        if (invalidParts.length > 0) {
            setError("Please enter valid part numbers for all items.");
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");

            // Prepare data for API
            const invoiceData = {
                ...formData,
                vendor: formData.useManualVendor ? formData.manualVendor : undefined,
                consignee: formData.useManualConsignee ? formData.manualConsignee : undefined,
                purchaser: formData.useManualPurchaser ? formData.manualPurchaser : undefined,
                vendorId: !formData.useManualVendor ? formData.vendorId : undefined,
                consigneeId: !formData.useManualConsignee ? formData.consigneeId : undefined,
                purchaserId: !formData.useManualPurchaser ? formData.purchaserId : undefined,
                items: manualParts.map(part => ({
                    partNumber: part.partNumber,
                    description: part.description || "",
                    hsCode: part.hsCode || "",
                    countryOfOrigin: part.countryOfOrigin || "",
                    quantity: part.quantity,
                    unitPrice: part.unitPrice,
                    usmca: part.usmca || "No",
                    usmcaOriginCriteria: part.usmcaOriginCriteria || "",
                    usmcaQualificationMethod: part.usmcaQualificationMethod || "",
                })),
                // Ensure FormExtras data is included
                deductions: {
                    transportationToCanada: formData.deductions.transportationToCanada || "",
                    constructionCosts: formData.deductions.constructionCosts || "",
                    exportPackaging: formData.deductions.exportPackaging || ""
                },
                additions: {
                    transportationToDirectJourney: formData.additions.transportationToDirectJourney || "",
                    commissions: formData.additions.commissions || "",
                    exportPacking: formData.additions.exportPacking || ""
                },
                other: {
                    royaltyPayments: formData.other.royaltyPayments || false,
                    suppliedGoods: formData.other.suppliedGoods || false
                },
                useLogo: useLogo,
                usmcaSignaturePermission: formData.usmcaSignaturePermission
            };

            // Make request to generate PDF
            const response = await api.post("/cci/generate", invoiceData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'blob' // Important for receiving binary data
            });

            // Create a download link for the PDF
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `canada_customs_invoice.pdf`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            // Clear draft after successful generation
            clearDraft();

        } catch (err) {
            console.error("Error generating CCI:", err);
            setError("Failed to generate Canada Customs Invoice. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };


    const resetForm = () => {
        if (hasDraft && window.confirm("This will clear your saved draft. Continue?")) {
            clearDraft();
        }

        setManualParts([]);
        setUseLogo(false);
        setFormData({
            vendorId: "",
            consigneeId: "",
            purchaserId: "",
            manualVendor: {
                name: "",
                address: "",
                city: "",
                stateProvince: "",
                postalCode: "",
                country: ""
            },
            manualConsignee: {
                name: "",
                address: "",
                city: "",
                stateProvince: "",
                postalCode: "",
                country: ""
            },
            manualPurchaser: {
                name: "",
                address: "",
                city: "",
                stateProvince: "",
                postalCode: "",
                country: ""
            },
            useManualVendor: false,
            useManualConsignee: false,
            useManualPurchaser: false,
            dateOfDirectShipment: new Date().toISOString().split('T')[0],
            currency: "USD",
            shipmentDetails: {
                transportation: "",
                otherReferences: "",
                countryOfTransshipment: "",
                conditionsOfSale: "",
                netWeight: undefined,
                grossWeight: undefined,
                weightUnit: "kg"
            },
            deductions: {
                transportationToCanada: "",
                constructionCosts: "",
                exportPackaging: ""
            },
            additions: {
                transportationToDirectJourney: "",
                commissions: "",
                exportPacking: ""
            },
            other: {
                royaltyPayments: false,
                suppliedGoods: false
            },
            usmcaSignaturePermission: false
        });
    };

    // If viewport is too small, render notification instead of form
    if (isViewportTooSmall) {
        return (
            <div className="w-full">
                <h2 className="text-xl font-bold mb-4">Customs Invoice Creation</h2>
                <DeviceCompatibilityNotice
                    minWidth={MINIMUM_VIEWPORT_WIDTH}
                    customMessage="The Customs Invoice Creation requires a larger screen for optimal use. Please switch to a desktop or laptop computer."
                />
            </div>
        );
    }

    return (
        <div className="p-3 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Customs Invoice Creation</h2>

            <DraftNotification
                hasDraft={hasDraft}
                onClearDraft={clearDraft}
            />

            <ErrorNotification error={error} />

            {loading ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 text-center">
                    Loading data...
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                        <h3 className="text-lg font-medium mb-3 sm:mb-4">Invoice Information</h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Vendor Section */}
                            <EntitySection
                                title="Vendor"
                                entityType="vendor"
                                entityId={formData.vendorId}
                                manualEntity={formData.manualVendor}
                                useManualEntry={formData.useManualVendor}
                                dropdownOptions={vendorOptions}
                                onToggleManualEntry={handleToggleManualEntry}
                                onDropdownChange={handleDropdownChange}
                                onManualEntityChange={handleManualEntityChange}
                            />

                            <InvoiceInfoSection
                                dateOfDirectShipment={formData.dateOfDirectShipment}
                                onInputChange={handleInputChange}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                            {/* Consignee Section */}
                            <EntitySection
                                title="Consignee"
                                entityType="consignee"
                                entityId={formData.consigneeId}
                                manualEntity={formData.manualConsignee}
                                useManualEntry={formData.useManualConsignee}
                                dropdownOptions={consigneeOptions}
                                onToggleManualEntry={handleToggleManualEntry}
                                onDropdownChange={handleDropdownChange}
                                onManualEntityChange={handleManualEntityChange}
                            />

                            {/* Purchaser Section */}
                            <EntitySection
                                title="Purchaser"
                                entityType="purchaser"
                                entityId={formData.purchaserId}
                                manualEntity={formData.manualPurchaser}
                                useManualEntry={formData.useManualPurchaser}
                                dropdownOptions={purchaserOptions}
                                onToggleManualEntry={handleToggleManualEntry}
                                onDropdownChange={handleDropdownChange}
                                onManualEntityChange={handleManualEntityChange}
                            />
                        </div>
                    </div>

                    <ShipmentDetailsSection
                        shipmentDetails={formData.shipmentDetails}
                        onInputChange={handleInputChange}
                    />

                    {hasPremiumAccess() ? (
                        <LogoSection
                            useLogo={useLogo}
                            hasLogo={hasLogo}
                            onLogoChange={handleLogoChange}
                            onUseLogoToggle={setUseLogo}
                        />
                    ) : (
                        // Show upgrade notice for non-premium users
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                            <h3 className="text-lg font-medium mb-3">Branded Document Creation</h3>
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                            Premium Feature
                                        </h4>
                                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                            Add your company logo to customs invoices with a Premium subscription.
                                        </p>
                                        <div className="mt-3">
                                            <button
                                                type="button"
                                                onClick={() => window.location.href = '/account#subscription'}
                                                className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md transition-colors"
                                            >
                                                Upgrade to Premium
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <FormExtras
                        deductions={formData.deductions}
                        additions={formData.additions}
                        other={formData.other}
                        onDeductionsChange={handleDeductionsChange}
                        onAdditionsChange={handleAdditionsChange}
                        onOtherChange={handleOtherChange}
                    />

                    {/* USMCA Digital Signature Permission - Professional and Premium only */}
                    {hasProfessionalOrPremiumAccess() ? (
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                            <h3 className="text-lg font-medium mb-3">USMCA Certificate of Origin</h3>

                            <div className="flex items-start">
                                <input
                                    type="checkbox"
                                    id="usmcaSignaturePermission"
                                    checked={formData.usmcaSignaturePermission || false}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        usmcaSignaturePermission: e.target.checked
                                    }))}
                                    className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700"
                                />
                                <label
                                    htmlFor="usmcaSignaturePermission"
                                    className="ml-3 text-sm text-gray-700 dark:text-gray-300"
                                >
                                    I authorize the use of my digital signature on the USMCA Certificate of Origin form
                                </label>
                            </div>
                        </div>
                    ) : (
                        // Show upgrade notice for Basic tier users
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
                            <h3 className="text-lg font-medium mb-3">USMCA Certificate of Origin</h3>
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                            Professional Feature
                                        </h4>
                                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                            Generate USMCA Certificate of Origin forms with Professional or Premium subscription.
                                        </p>
                                        <div className="mt-3">
                                            <button
                                                type="button"
                                                onClick={() => window.location.href = '/account#subscription'}
                                                className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md transition-colors"
                                            >
                                                Upgrade to Professional
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}



                    <InvoicePartsSection
                        parts={parts}
                        manualParts={manualParts}
                        currency={formData.currency}
                        onAddPart={addNewPart}
                        onRemovePart={removePart}
                        onUpdatePart={updatePart}
                        onUpdatePartFull={updatePartFull}
                        onCurrencyChange={handleCurrencyChange}
                        onAddMultipleParts={addMultipleParts}
                    />

                    <FormActions
                        isGenerating={isGenerating}
                        hasItems={manualParts.length > 0}
                        onReset={resetForm}
                    />
                </form>
            )}
        </div>
    );
};

